import { useState } from 'react';
import { AttentionTriangleIcon } from '@rsos/assets/icons';
import { Text, Modal, Button } from '@rsos/base-components';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/base-components/Modal/ModalComponents';
import { useTransferTCAPIConversationMutation } from '@rsos/reduxDataStore/src/features/conversations/tcapiAPI';
import AgencyListItem from './AgencyListItem';
import AgencySearchInput from './AgencySearchInput';
import TransferInfoBanner from './TransferInfoBanner';
import { AgencyList, ErrorBanner } from './TransferToAgencyModal.styles';
import type { Agency } from './types';

interface TransferToAgencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  callerID: string | null;
  orgName: string | undefined;
  agencies: Agency[];
  isLoadingAgencies?: boolean;
  isError?: boolean;
}

function TransferToAgencyModal({
  isOpen,
  onClose,
  callerID,
  orgName,
  agencies,
  isLoadingAgencies,
  isError,
}: TransferToAgencyModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);
  const [isTransferError, setIsTransferError] = useState(false);

  const [transferConversation, transferStates] =
    useTransferTCAPIConversationMutation();

  const filteredAgencies = agencies.filter(agency =>
    agency.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleClose = () => {
    setSearchQuery('');
    setSelectedAgencyId(null);
    setIsTransferError(false);
    onClose();
  };

  const handleInitiateTransfer = () => {
    if (!selectedAgencyId || !callerID || !orgName) return;

    const selectedAgency = agencies.find(
      agency => agency.identifier === selectedAgencyId,
    );
    if (!selectedAgency) {
      // should never ever happen since the selected id is from the list of agencies, but just in case:
      throw new Error('Selected agency not found');
    }

    setIsTransferError(false);
    transferConversation({
      callerID,
      orgName,
      receiving_agency_identifier: selectedAgency.identifier,
      receiving_agency_name: selectedAgency.name,
    })
      .unwrap()
      .then(() => {
        handleClose();
      })
      .catch((err: unknown) => {
        setIsTransferError(true);
        // eslint-disable-next-line no-console -- Log transfer failures so they surface in Sentry
        console.error('Failed to initiate transfer:', err);
      });
  };

  return (
    <Modal
      isOpened={isOpen}
      onClose={handleClose}
      width="400px"
      padding="0"
      fitContent
      id="modal-transfer-to-agency"
    >
      <ModalHeader
        onClose={handleClose}
        padding="24px 24px 16px"
        borderBottom="1px solid"
        borderColor="lightLineDivider"
      >
        <Text size="xlarge" color="primaryText" mb={1}>
          Transfer to Agency
        </Text>
        <Text size="small" color="secondaryText">
          Select destination ECC to transfer
        </Text>
      </ModalHeader>
      <ModalBody px="24px" mb={0}>
        <AgencySearchInput value={searchQuery} onChange={setSearchQuery} />
        <AgencyList>
          {isLoadingAgencies && (
            <Text
              size="small"
              color="secondaryText"
              p="16px"
              textAlign="center"
            >
              Loading agencies...
            </Text>
          )}
          {isError && (
            <Text size="small" color="lightDanger" p="16px" textAlign="center">
              Failed to load agencies. Please try again.
            </Text>
          )}
          {!isLoadingAgencies && !isError && filteredAgencies.length === 0 && (
            <Text
              size="small"
              color="secondaryText"
              p="16px"
              textAlign="center"
            >
              No agencies available
            </Text>
          )}
          {!isLoadingAgencies &&
            !isError &&
            filteredAgencies.map(agency => (
              <AgencyListItem
                key={agency.identifier}
                agency={agency}
                isSelected={selectedAgencyId === agency.identifier}
                onSelect={setSelectedAgencyId}
              />
            ))}
        </AgencyList>
      </ModalBody>
      <ModalFooter
        py="16px"
        px="24px"
        borderTop="1px solid"
        borderColor="lightLineDivider"
        stacked
        flow={12}
      >
        {isTransferError && (
          <ErrorBanner>
            <AttentionTriangleIcon width={14} height={14} color="lightDanger" />
            Failed to initiate transfer. Please try again.
          </ErrorBanner>
        )}
        {selectedAgencyId && !isTransferError && <TransferInfoBanner />}
        <div style={{ display: 'flex', gap: 12, width: '100%' }}>
          <Button btnType="tertiary" grow onClick={handleClose}>
            Cancel
          </Button>
          <Button
            btnType="primary"
            grow
            onClick={handleInitiateTransfer}
            disabled={!selectedAgencyId}
            isLoadingProp={transferStates.isLoading}
          >
            Initiate Transfer
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default TransferToAgencyModal;
